import type { FunctionComponent } from "react";
import { graphql, useFragment } from "react-relay";

import { BaseLinkContextProvider } from "shared/components/common/base-link/context";
import { useTrackImpressionEventByIntersection } from "shared/hooks";

import { PlusArchiveTitle } from "scmp-app/components/plus/archive/archive-title";
import { usePlusArticlePopup } from "scmp-app/components/plus/article-popup/hooks";
import { usePlusDetailedListingPopup } from "scmp-app/components/plus/detailed-listing-popup/hooks";
import { sendGA4Tracking } from "scmp-app/components/tracking/google-analytics-4/apis";
import type { RecirculationWidgetImpressionEvent } from "scmp-app/components/tracking/google-analytics-4/types";
import type { quickDigestPlusArchiveQuery$key } from "scmp-app/queries/__generated__/quickDigestPlusArchiveQuery.graphql";

import { Container, ContentItemContainer, StyledContentItemPlusSecondary } from "./styles";

type Props = {
  className?: string;
  reference: quickDigestPlusArchiveQuery$key;
};
export const PlusArchiveQuickDigest: FunctionComponent<Props> = ({ className, reference }) => {
  const content = useFragment(
    graphql`
      fragment quickDigestPlusArchiveQuery on Query
      @argumentDefinitions(quickDigestTypeUuid: { type: "String!" }) {
        pulseArchiveQuickDigest: articles(
          filter: { typeIds: [$quickDigestTypeUuid] }
          first: 2
          orderBy: { field: PUBLISHED_DATE, direction: DESC }
        ) {
          edges {
            node {
              entityId
              ...plusSecondaryContentItemContent
              ...hooksPlusArticleLinkHandlerArticle
            }
          }
        }
      }
    `,
    reference,
  );

  const { handleOpen: handleOpenDetailedListingPopup } = usePlusDetailedListingPopup();
  const { handleOpen: handleOpenArticlePopup } = usePlusArticlePopup();

  const { captureTrackImpressionEventTargetElement } =
    useTrackImpressionEventByIntersection<RecirculationWidgetImpressionEvent>({
      ga4TrackingHandler: sendGA4Tracking,
      getGa4Event: () => ({
        action: "imp",
        category: "recirculation",
        customized_parameters: {
          list_of_articles: (
            content?.pulseArchiveQuickDigest?.edges.map(edge => edge.node.entityId) ?? []
          ).join(","),
          page_type: "archive",
          widget_name: "quick_digest",
        },
        subcategory: "widget",
      }),
      options: {
        isSendGA4Tracking: true,
        shouldSendOnce: true,
      },
    });

  return (
    <BaseLinkContextProvider
      customQueryParameters={{
        module: "quick_digest",
      }}
    >
      <Container className={className} ref={captureTrackImpressionEventTargetElement}>
        <PlusArchiveTitle
          onClick={() =>
            handleOpenDetailedListingPopup({
              pgtype: "archive",
              type: "quick-digest",
            })
          }
          title="Quick Digest"
        />
        <ContentItemContainer>
          {content?.pulseArchiveQuickDigest?.edges.map(edge => (
            <StyledContentItemPlusSecondary
              key={edge.node.entityId}
              onClick={() =>
                handleOpenArticlePopup(edge.node, {
                  pageType: "archive",
                  widgetName: "quick_digest",
                })
              }
              reference={edge.node}
              withArticleType={false}
              withSummary
            />
          ))}
        </ContentItemContainer>
      </Container>
    </BaseLinkContextProvider>
  );
};

PlusArchiveQuickDigest.displayName = "PlusArchiveQuickDigest";
